import type { UnifiedCanvas } from '../modes/unified/UnifiedCanvas';
import type { UnifiedToolbar } from '../ui/UnifiedToolbar';
import { STORAGE_KEYS } from '../config/storageKeys';
import { TOOLBAR_ICONS } from '../ui/icons';

export interface ViewCallbacks {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
  onFitToScreen: () => void;
  onGridToggle: (enabled: boolean) => void;
  onSnapToggle: (enabled: boolean) => void;
  onInvertToggle: (enabled: boolean) => void;
  onShowShortcuts: () => void;
}

export interface ThemeContext {
  getToolbarEl: () => HTMLElement | null;
  getUnifiedCanvas: () => UnifiedCanvas | null;
  getUnifiedToolbar: () => UnifiedToolbar | null;
  viewCallbacks?: ViewCallbacks;
}

export class ThemeController {
  private readonly context: ThemeContext;
  private zoomDisplay: HTMLSpanElement | null = null;
  private gridBtn: HTMLButtonElement | null = null;
  private snapBtn: HTMLButtonElement | null = null;
  private invertBtn: HTMLButtonElement | null = null;
  private gridEnabled = true;
  private snapEnabled = true;
  private invertEnabled = false;
  private gridAutoEnabledBySnap = false;

  constructor(context: ThemeContext) {
    this.context = context;
  }

  createModeSwitcher(): void {
    const toolbarEl = this.context.getToolbarEl();
    if (!toolbarEl) return;

    const switcher = document.createElement('div');
    switcher.className = 'mode-switcher';

    // Title + theme toggle only (no mode buttons)
    const title = document.createElement('h1');
    title.className = 'app-title';
    title.textContent = '/drawlings/';
    switcher.appendChild(title);

    const spacer = document.createElement('div');
    spacer.className = 'mode-switcher-spacer';
    switcher.appendChild(spacer);

    // View controls (zoom, grid, snap, invert, help)
    if (this.context.viewCallbacks) {
      const viewControls = this.createViewControls(this.context.viewCallbacks);
      switcher.appendChild(viewControls);

      const divider = document.createElement('div');
      divider.className = 'header-divider';
      switcher.appendChild(divider);
    }

    const themeToggle = document.createElement('button');
    themeToggle.className = 'theme-toggle';
    themeToggle.title = 'Toggle Theme';
    const sunSvg = document.createRange().createContextualFragment(`<svg class="sun-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>`);
    const moonSvg = document.createRange().createContextualFragment(`<svg class="moon-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>`);
    themeToggle.appendChild(sunSvg);
    themeToggle.appendChild(moonSvg);
    themeToggle.addEventListener('click', (): void => {
      this.toggleTheme();
    });
    switcher.appendChild(themeToggle);

    // Insert before toolbar
    toolbarEl.parentElement?.insertBefore(switcher, toolbarEl);

    // Apply saved theme
    this.applyTheme();
  }

  toggleTheme(): void {
    const currentTheme = document.documentElement.dataset['theme'];
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    document.documentElement.dataset['theme'] = newTheme;
    localStorage.setItem(STORAGE_KEYS.theme, newTheme);

    // Sync canvas inversion with theme (dark = inverted, light = normal)
    const inverted = newTheme === 'dark';
    const canvas = this.context.getUnifiedCanvas();
    canvas?.setInverted(inverted);

    // Switch drawing color based on theme
    const drawColor = this.getThemeDrawingColor(inverted);
    canvas?.setColor(drawColor);
    this.context.getUnifiedToolbar()?.setColor(drawColor);
    this.context.getUnifiedToolbar()?.setInvertMode(inverted);
  }

  applyTheme(): void {
    const savedTheme = localStorage.getItem(STORAGE_KEYS.theme);
    if (savedTheme === 'light') {
      document.documentElement.dataset['theme'] = 'light';
    }
  }

  getInvertedFromTheme(): boolean {
    const theme = document.documentElement.dataset['theme'];
    return theme !== 'light';
  }

  getThemeDrawingColor(inverted: boolean): string {
    return inverted ? '#ffffff' : '#000000';
  }

  // --- View controls in header ---

  private createViewControls(callbacks: ViewCallbacks): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'header-view-controls';

    const ICONS = TOOLBAR_ICONS;

    container.appendChild(this.createHeaderBtn(ICONS.zoomOut, 'Zoom Out (Cmd -)', (): void => callbacks.onZoomOut()));

    this.zoomDisplay = document.createElement('span');
    this.zoomDisplay.className = 'zoom-display header-zoom-display';
    this.zoomDisplay.textContent = '100%';
    this.zoomDisplay.title = 'Click to reset (Cmd 0)';
    this.zoomDisplay.addEventListener('click', (): void => callbacks.onResetView());
    container.appendChild(this.zoomDisplay);

    container.appendChild(this.createHeaderBtn(ICONS.zoomIn, 'Zoom In (Cmd +)', (): void => callbacks.onZoomIn()));
    container.appendChild(this.createHeaderBtn(ICONS.fitToScreen, 'Fit to Screen', (): void => callbacks.onFitToScreen()));

    this.gridBtn = this.createHeaderBtn(ICONS.grid, 'Toggle Grid (G)', (): void => {
      const next = !this.gridEnabled;
      if (!next) this.gridAutoEnabledBySnap = false;
      this.setGridEnabled(next);
      callbacks.onGridToggle(next);
    });
    container.appendChild(this.gridBtn);
    this.setGridEnabled(this.gridEnabled);

    this.snapBtn = this.createHeaderBtn(ICONS.snap, 'Snap to Grid (Shift+S)', (): void => {
      const next = !this.snapEnabled;
      if (next && !this.gridEnabled) {
        this.gridAutoEnabledBySnap = true;
        this.setGridEnabled(true);
        callbacks.onGridToggle(true);
      }
      if (!next && this.gridAutoEnabledBySnap) {
        this.gridAutoEnabledBySnap = false;
        this.setGridEnabled(false);
        callbacks.onGridToggle(false);
      }
      this.setSnapEnabled(next);
      callbacks.onSnapToggle(next);
    });
    container.appendChild(this.snapBtn);
    this.setSnapEnabled(this.snapEnabled);

    this.invertBtn = this.createHeaderBtn(ICONS.invert, 'Invert Canvas (I)', (): void => {
      const next = !this.invertEnabled;
      this.setInvertEnabled(next);
      callbacks.onInvertToggle(next);
    });
    container.appendChild(this.invertBtn);
    this.setInvertEnabled(this.invertEnabled);

    container.appendChild(this.createHeaderBtn(ICONS.help, 'Keyboard Shortcuts (?)', (): void => callbacks.onShowShortcuts()));

    return container;
  }

  private createHeaderBtn(iconSvg: string, title: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'action-btn header-action-btn';
    btn.title = title;
    const fragment = document.createRange().createContextualFragment(iconSvg);
    btn.appendChild(fragment);
    btn.addEventListener('click', onClick);
    return btn;
  }

  // --- Public API for syncing view state ---

  updateZoom(scale: number): void {
    if (this.zoomDisplay) {
      this.zoomDisplay.textContent = `${Math.round(scale * 100)}%`;
    }
  }

  setGridEnabled(enabled: boolean): void {
    this.gridEnabled = enabled;
    this.gridBtn?.classList.toggle('active', enabled);
  }

  setSnapEnabled(enabled: boolean): void {
    this.snapEnabled = enabled;
    this.snapBtn?.classList.toggle('snap-active', enabled);
    // Remove active if not snap-active
    if (!enabled) this.snapBtn?.classList.remove('snap-active');
  }

  setInvertEnabled(enabled: boolean): void {
    this.invertEnabled = enabled;
    this.invertBtn?.classList.toggle('active', enabled);
  }
}
